/**
 * VerbIcon — Universal verb indicator used across all permission views.
 *
 * Renders a small colored icon dot representing a permission verb.
 * Same visual language everywhere: consent, realm summary, EP viewer, DLR preview.
 *
 * Variants (CSS class suffix → color):
 *   read    → blue
 *   create  → green
 *   edit    → amber
 *   delete  → red
 *   propose → teal
 *   see     → purple (relationship read)
 *
 * In interactive mode (onClick provided), acts as a toggle button.
 */
import { Eye, Plus, Pencil, Trash2, Lightbulb } from 'lucide-react';

const ICONS = {
  read: Eye,
  create: Plus,
  edit: Pencil,
  delete: Trash2,
  propose: Lightbulb,
  see: Eye,
};

const LABELS = {
  read: 'Read',
  create: 'Create new',
  edit: 'Edit',
  delete: 'Delete',
  propose: 'Suggest changes (you approve)',
  see: 'View',
};

/**
 * Map from verb to CSS variant class suffix.
 */
export const VERB_VARIANTS = {
  read: 'read',
  create: 'create',
  edit: 'edit',
  delete: 'delete',
  propose: 'propose',
  see: 'see',
};

export default function VerbIcon({ verb, variant, schemaName, size = 13, onClick, isOn = true, disabled = false }) {
  const Icon = ICONS[verb] || Eye;
  const label = LABELS[verb] || verb;
  const tooltip = schemaName ? `${label} ${schemaName}` : label;
  const v = variant || VERB_VARIANTS[verb] || verb;

  const cls = [
    'perm-vi',
    `perm-vi--${v}`,
    onClick ? 'perm-vi--interactive' : '',
    !isOn ? 'perm-vi--off' : '',
    disabled ? 'perm-vi--disabled' : '',
  ].filter(Boolean).join(' ');

  if (onClick) {
    return (
      <button
        type="button"
        className={cls}
        title={tooltip}
        onClick={disabled ? undefined : onClick}
        disabled={disabled}
      >
        <Icon size={size} />
      </button>
    );
  }

  return (
    <span className={cls} title={tooltip}>
      <Icon size={size} />
    </span>
  );
}
